import React from 'react';
import { X, Award, Calendar, Users, MapPin, Target, PenTool, Clock } from 'lucide-react';

const MilestoneDetailsModal = ({ milestone, onClose }) => {
  if (!milestone) return null;

  // Format date nicely
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 9999, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
      <div className="bg-white dark:bg-dark-200 rounded-lg p-4 w-11/12 max-w-lg relative">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold dark:text-darkText-primary flex items-center">
            <Award size={20} className="text-yellow-500 mr-2" />
            Milestone Details
          </h3>
          <button 
            onClick={onClose} 
            className="text-gray-500 dark:text-darkText-secondary hover:bg-gray-100 dark:hover:bg-dark-300 p-1 rounded-full"
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-lg mb-4">
          <div className="flex justify-center mb-3">
            <div className="h-16 w-16 rounded-full bg-blue-500 flex items-center justify-center">
              {milestone.type === 'batting' ? (
                <Target size={32} className="text-white" />
              ) : (
                <PenTool size={32} className="text-white" />
              )}
            </div>
          </div>
          
          <div className="text-center mb-3">
            <h4 className="font-bold text-xl text-blue-800 dark:text-blue-300 mb-1">{milestone.title}</h4>
            <p className="text-blue-600 dark:text-blue-400">{milestone.description}</p>
          </div>
          
          <div className="flex justify-center mb-2">
            <span className="text-xs px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 capitalize">
              {milestone.category} milestone
            </span>
          </div>
        </div>
        
        {milestone.achieved ? (
          <div className="space-y-4">
            <div className="border-b border-gray-200 dark:border-dark-400 pb-3">
              <h4 className="font-medium text-green-600 dark:text-green-400 mb-2 flex items-center">
                <Award size={16} className="mr-2" />
                Achievement Details
              </h4>
              
              <div className="space-y-2">
                <div className="flex items-start">
                  <Calendar size={16} className="mr-2 text-gray-500 dark:text-darkText-tertiary mt-0.5" />
                  <div>
                    <p className="text-sm font-medium dark:text-darkText-primary">Date Achieved</p>
                    <p className="text-sm text-gray-600 dark:text-darkText-secondary">
                      {formatDate(milestone.achievedDate)}
                    </p>
                  </div>
                </div>
                
                {milestone.gameDetails && (
                  <>
                    <div className="flex items-start">
                      <Users size={16} className="mr-2 text-gray-500 dark:text-darkText-tertiary mt-0.5" />
                      <div>
                        <p className="text-sm font-medium dark:text-darkText-primary">Opponent</p>
                        <p className="text-sm text-gray-600 dark:text-darkText-secondary">
                          {milestone.gameDetails.opponent || 'Not recorded'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <MapPin size={16} className="mr-2 text-gray-500 dark:text-darkText-tertiary mt-0.5" />
                      <div>
                        <p className="text-sm font-medium dark:text-darkText-primary">Location</p>
                        <p className="text-sm text-gray-600 dark:text-darkText-secondary">
                          {milestone.gameDetails.location || 'Not recorded'}
                          {milestone.gameDetails.isHome !== undefined && (
                            <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${
                              milestone.gameDetails.isHome 
                                ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400' 
                                : 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                            }`}>
                              {milestone.gameDetails.isHome ? 'HOME' : 'AWAY'}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    
                    {milestone.gameDetails.stats && (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-dark-400">
                        <p className="text-sm font-medium mb-2 dark:text-darkText-primary">Performance in this game:</p>
                        <div className="bg-gray-50 dark:bg-dark-300 p-3 rounded-md">
                          {milestone.type === 'batting' ? (
                            <p className="text-sm text-gray-700 dark:text-darkText-secondary">
                              {milestone.gameDetails.stats.hits || 0}/{milestone.gameDetails.stats.atBats || 0}, 
                              {' '}{milestone.gameDetails.stats.runs || 0} R, 
                              {' '}{milestone.gameDetails.stats.rbi || 0} RBI
                              {milestone.gameDetails.stats.homeRuns > 0 && `, ${milestone.gameDetails.stats.homeRuns} HR`}
                            </p>
                          ) : (
                            <p className="text-sm text-gray-700 dark:text-darkText-secondary">
                              {milestone.gameDetails.stats.inningsPitched || 0} IP, 
                              {' '}{milestone.gameDetails.stats.strikeouts || 0} K, 
                              {' '}{milestone.gameDetails.stats.earnedRuns || 0} ER
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            
            <div className="border-b border-gray-200 dark:border-dark-400 pb-3">
              <h4 className="font-medium text-gray-700 dark:text-darkText-primary mb-2 flex items-center">
                <Target size={16} className="mr-2 text-blue-500" />
                Milestone Criteria
              </h4>
              
              <div className="space-y-1">
                {milestone.criteria && milestone.criteria.map((criterion, index) => (
                  <div key={index} className="text-sm text-gray-600 dark:text-darkText-secondary flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                    {criterion.stat}: {criterion.operator} {criterion.value}
                    {criterion.unit && ` (${criterion.unit})`}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="border-b border-gray-200 dark:border-dark-400 pb-3">
              <h4 className="font-medium text-yellow-600 dark:text-yellow-400 mb-2 flex items-center">
                <Clock size={16} className="mr-2" />
                Progress Tracking
              </h4>
              
              <div className="mb-3">
                <div className="w-full bg-gray-200 dark:bg-dark-400 rounded-full h-2.5 mb-1">
                  <div 
                    className="bg-blue-500 dark:bg-blue-600 h-2.5 rounded-full" 
                    style={{ width: `${milestone.progress || 0}%` }}
                  ></div>
                </div>
                <p className="text-right text-xs text-gray-500 dark:text-darkText-tertiary">
                  {(milestone.progress || 0).toFixed(0)}% complete
                </p>
              </div>
            </div>
            
            <div className="border-b border-gray-200 dark:border-dark-400 pb-3">
              <h4 className="font-medium text-gray-700 dark:text-darkText-primary mb-2 flex items-center">
                <Target size={16} className="mr-2 text-blue-500" />
                Milestone Criteria
              </h4>
              
              <div className="space-y-1">
                {milestone.criteria && milestone.criteria.map((criterion, index) => (
                  <div key={index} className="text-sm text-gray-600 dark:text-darkText-secondary flex items-center">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                    {criterion.stat}: {criterion.operator} {criterion.value}
                    {criterion.unit && ` (${criterion.unit})`}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        
        <div className="flex justify-end pt-4">
          <button 
            onClick={onClose}
            className="bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white py-2 px-4 rounded transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default MilestoneDetailsModal;
